import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Grid, Typography, Card, CardContent, Button, Stack, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  List, ListItem, ListItemIcon, ListItemText, Paper
} from '@mui/material';
import { MessageCircle, Smartphone, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { getProducts, createSubscription, renewSubscription } from '../api/subscriptions.api';

const ENABLE_WALLET = false;

export default function ServiceActivationPage() {
  const { user, activeApp: reduxActiveApp } = useSelector((state) => state.auth);

  let currentApp = reduxActiveApp;
  if (!currentApp) {
    try {
      const bootstrapped = sessionStorage.getItem('bootstrappedApp');
      if (bootstrapped) {
        currentApp = JSON.parse(bootstrapped);
      } else {
        const storedActive = localStorage.getItem('activeApp');
        if (storedActive) {
          currentApp = JSON.parse(storedActive);
        }
      }
    } catch (e) {
      // ignore storage error
    }
  }

  const isOwner = !currentApp || (
    currentApp.owner === user?._id || 
    currentApp.owner === user?.id ||
    (currentApp.owner && typeof currentApp.owner === 'object' && (currentApp.owner._id === user?._id || currentApp.owner.id === user?._id))
  );
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'whatsapp' or 'ussd'
  const [formData, setFormData] = useState({
    phoneNumber: '',
    provider: '',
    preferredCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = form, 2 = payment progress, 3 = success
  // Load products from backend on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getProducts();
        if (response.ok && response.data?.success) {
          setProducts(response.data.data || []);
        } else {
          toast.error('Failed to load available feature products');
        }
      } catch (err) {
        toast.error('Error fetching subscription products');
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter products for displaying pricing dynamically
  const whatsappProduct = products.find(p => p.type === 'whatsapp' && p.billingCycle === 'monthly');
  const ussdProduct = products.find(p => p.type === 'ussd' && p.billingCycle === 'monthly');

  const handleOpen = (type) => {
    if (!currentApp) {
      toast.warn('Please select or manage an app workspace first');
      return;
    }
    setModalType(type);
    setStep(1);
    setFormData({ phoneNumber: '', provider: '', preferredCode: '' });
    setOpenModal(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleActivate = async () => {
    const targetProduct = modalType === 'whatsapp' ? whatsappProduct : ussdProduct;
    if (!targetProduct) {
      toast.error('Service product config not found in database');
      return;
    }

    if (modalType === 'whatsapp' && !formData.phoneNumber) {
      toast.error('Please enter a WhatsApp phone number');
      return;
    }
    if (modalType === 'ussd' && !formData.provider) {
      toast.error('Please specify your telecom provider');
      return;
    }

    setStep(2); // Go to loader
    setLoading(true);

    try {
      // 1. Create the subscription (generates the pending-payment invoice)
      const createPayload = {
        productId: targetProduct._id,
        appId: currentApp._id,
        metadata: {
          phoneNumber: modalType === 'whatsapp' ? formData.phoneNumber : undefined,
          provider: modalType === 'ussd' ? formData.provider : undefined,
          shortCode: modalType === 'ussd' ? formData.preferredCode : undefined,
        }
      };

      const createRes = await createSubscription(createPayload);
      if (!createRes.ok || !createRes.data?.success) {
        throw new Error(createRes.data?.message || 'Failed to order subscription');
      }

      if (ENABLE_WALLET) {
        const subscriptionId = createRes.data.data._id;
        // 2. Pay the setup invoice using user's wallet funds
        const payRes = await renewSubscription(subscriptionId);
        if (!payRes.ok || !payRes.data?.success) {
          throw new Error(payRes.data?.message || 'Failed to settle setup invoice using wallet');
        }
      }

      setLoading(false);
      setStep(3); // Success Screen
      toast.success(ENABLE_WALLET ? 'Service activated successfully!' : 'Service ordered successfully! Set up pending admin approval.');
    } catch (err) {
      setLoading(false);
      setStep(1); // Return to form
      toast.error(err.message || 'An error occurred during activation');
    }
  };

  if (loadingProducts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (currentApp && !isOwner) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Paper variant="outlined" sx={{ p: 6, borderRadius: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
          <Typography variant="h5" color="error" fontWeight={800} sx={{ mb: 2 }}>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Only the owner of this application workspace has permission to activate services and manage subscriptions.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: 4 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
          Feature Add-ons & Activations
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Enable premium integrations for <strong>{currentApp?.name || 'your app'}</strong> to unlock new channels.
        </Typography>

        <Grid container spacing={4}>
          {/* WhatsApp API Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <Box sx={{ bgcolor: '#25D366', p: 3, color: 'white' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <MessageCircle size={32} />
                    <Typography variant="h5" fontWeight={700}>WhatsApp API Gateway</Typography>
                  </Stack>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>Setup Fee</Typography>
                    <Typography variant="h6" fontWeight={800}>
                      ₦{whatsappProduct?.setupFee?.toLocaleString() || '200,000'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Automate notifications, customer balance updates, transaction receipts, and customer care query resolution directly on WhatsApp.
                </Typography>

                <List sx={{ mb: 4 }}>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}><Check size={18} color="#25D366" /></ListItemIcon>
                    <ListItemText primary="Automated Bill-payment Receipts" primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}><Check size={18} color="#25D366" /></ListItemIcon>
                    <ListItemText primary="WhatsApp Customer Support Bot" primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}><Check size={18} color="#25D366" /></ListItemIcon>
                    <ListItemText primary="Verified Business Profile Link" primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                </List>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">Subscription Cost</Typography>
                    <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
                      ₦{whatsappProduct?.price?.toLocaleString() || '30,000'} / month
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => handleOpen('whatsapp')}
                    sx={{ bgcolor: '#25D366', color: 'white', '&:hover': { bgcolor: '#128C7E' }, borderRadius: 2 }}
                  >
                    Activate Feature
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* USSD Service Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <Box sx={{ bgcolor: '#5D5FEF', p: 3, color: 'white' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Smartphone size={32} />
                    <Typography variant="h5" fontWeight={700}>USSD Offline Service</Typography>
                  </Stack>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>Setup Fee</Typography>
                    <Typography variant="h6" fontWeight={800}>
                      ₦{ussdProduct?.setupFee?.toLocaleString() || '200,000'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Provide offline banking utility. Customers can check balances, buy data bundles, pay utility bills, and transfer funds without an internet connection.
                </Typography>

                <List sx={{ mb: 4 }}>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}><Check size={18} color="#5D5FEF" /></ListItemIcon>
                    <ListItemText primary="Offline Dial Code (e.g. *384*AppCode#)" primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}><Check size={18} color="#5D5FEF" /></ListItemIcon>
                    <ListItemText primary="High-performance Session Gateway" primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}><Check size={18} color="#5D5FEF" /></ListItemIcon>
                    <ListItemText primary="Cross-carrier Compatibility (MTN, Airtel, Glo)" primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                </List>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">Subscription Cost</Typography>
                    <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
                      ₦{ussdProduct?.price?.toLocaleString() || '60,000'} / month
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => handleOpen('ussd')}
                    sx={{ bgcolor: '#5D5FEF', color: 'white', '&:hover': { bgcolor: '#4B4DCC' }, borderRadius: 2 }}
                  >
                    Activate Feature
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Activation Dialog Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, p: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {step === 1 && `Configure ${modalType === 'whatsapp' ? 'WhatsApp Gateway' : 'USSD Shortcode'}`}
          {step === 2 && 'Processing Setup Payment'}
          {step === 3 && 'Activation Requested!'}
        </DialogTitle>
        <DialogContent>
          {step === 1 && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                To link this channel, enter the initial configuration settings below. You will make a checkout payment of <strong>₦{(modalType === 'whatsapp' ? whatsappProduct : ussdProduct)?.setupFee?.toLocaleString()}</strong> (activation setup fee).
              </Typography>

              {modalType === 'whatsapp' ? (
                <TextField
                  label="WhatsApp Phone Number"
                  name="phoneNumber"
                  placeholder="+2348000000000"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              ) : (
                <Stack spacing={2.5}>
                  <TextField
                    label="Telecom Carrier / Provider"
                    name="provider"
                    placeholder="e.g. MTN Shared"
                    value={formData.provider}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Preferred USSD Sub-Code (Optional)"
                    name="preferredCode"
                    placeholder="e.g. *384*555#"
                    value={formData.preferredCode}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Stack>
              )}
            </Stack>
          )}

          {step === 2 && (
            <Stack alignItems="center" spacing={3} sx={{ py: 4 }}>
              <CircularProgress color={modalType === 'whatsapp' ? 'success' : 'primary'} />
              <Box textAlign="center">
                <Typography variant="h6" fontWeight={600}>
                  {ENABLE_WALLET ? 'Authorizing Payment...' : 'Placing Order...'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {ENABLE_WALLET 
                    ? 'Deducting setup fee from your account wallet balance. Please do not close this modal.' 
                    : 'Submitting setup request. Please do not close this modal.'}
                </Typography>
              </Box>
            </Stack>
          )}

          {step === 3 && (
            <Stack alignItems="center" spacing={3} sx={{ py: 3, textAlign: 'center' }}>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: modalType === 'whatsapp' ? '#e8f5e9' : '#e8eaf6',
                color: modalType === 'whatsapp' ? '#2e7d32' : '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ShieldCheck size={36} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="text.primary">Activation Pending Setup</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 2 }}>
                  {ENABLE_WALLET 
                    ? 'Your setup payment was debited successfully. The configuration is now **Pending Admin Setup**.'
                    : 'Your service request has been submitted successfully. The configuration is now **Pending Admin Setup**.'}
                  Our provisioning team will link your {modalType === 'whatsapp' ? 'WhatsApp API' : 'USSD shortcode'} within 24 hours and activate your billing period.
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {step === 1 && (
            <>
              <Button onClick={() => setOpenModal(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleActivate} sx={{ borderRadius: 2 }}>
                {ENABLE_WALLET 
                  ? `Confirm & Pay ₦${(modalType === 'whatsapp' ? whatsappProduct : ussdProduct)?.setupFee?.toLocaleString()}`
                  : 'Confirm & Order'}
              </Button>
            </>
          )}
          {step === 3 && (
            <Button variant="contained" onClick={() => setOpenModal(false)} sx={{ borderRadius: 2, px: 4 }}>
              Got It
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
